/**
 * Gemeldete Ausnahmen — Pausen mit Grund-Eingabe bei früher Pause.
 *
 * Zeigt alle Pause-Stempel mit ist_ausnahme=true (MA stempelte vor Ablauf
 * der Mindest-Arbeitszeit auf Pause und hat einen Grund angegeben).
 *
 * @page /anwesenheiten/ausnahmen
 * @roles admin, manager
 * @api GET /api/anwesenheiten/ausnahmen
 * @i18n ausnahmen
 * @since v2.350.0
 */
import React, { useEffect, useState } from 'react';
import { PageHeader } from '../components/ui';
import MitarbeiterAvatar from '../components/MitarbeiterAvatar';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

function pad2(n) { return String(n).padStart(2, '0'); }
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function fmtDateTime(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Vienna',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const TYP_LABELS = {
  kurze_pause: 'Kurzpause',
  mittagspause: 'Mittagspause',
};

export default function AusnahmenPage() {
  const { t } = useLanguage();
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [maFilter, setMaFilter] = useState('');
  const [grundFilter, setGrundFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mitarbeiter, setMitarbeiter] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/mitarbeiter?ist_aktiv=true`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setMitarbeiter((d.data || []).filter(m => m.typ === 'person')))
      .catch(() => setMitarbeiter([]));
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (maFilter) params.set('ma_id', maFilter);
    if (grundFilter) params.set('grund', grundFilter);
    fetch(`${API}/api/anwesenheiten/ausnahmen?${params.toString()}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { ausnahmen: [], gesamt: 0, top_gruende: [], top_mitarbeiter: [] })
      .then(setData)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-page">
      <PageHeader
        title={t('ausnahmen.titel', 'Gemeldete Ausnahmen')}
        subtitle={t('ausnahmen.subtitle', 'Pausen mit Grund-Eingabe (vor Ablauf der Mindest-Arbeitszeit)')}
      />

      {/* Filter */}
      <div className="bg-surface-elevated rounded-card border border-line mb-4">
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('ausnahmen.von', 'Von')}</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border"
              style={{ borderColor: 'var(--border-line, #E5E7EB)', background: 'var(--bg-surface, white)', color: 'var(--text-primary, #1A1A2E)' }} />
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('ausnahmen.bis', 'Bis')}</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border"
              style={{ borderColor: 'var(--border-line, #E5E7EB)', background: 'var(--bg-surface, white)', color: 'var(--text-primary, #1A1A2E)' }} />
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('ausnahmen.mitarbeiter', 'Mitarbeiter')}</label>
            <select value={maFilter} onChange={e => setMaFilter(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border"
              style={{ borderColor: 'var(--border-line, #E5E7EB)', background: 'var(--bg-surface, white)', color: 'var(--text-primary, #1A1A2E)' }}>
              <option value="">{t('ausnahmen.alle', 'Alle')}</option>
              {mitarbeiter.map(m => (
                <option key={m.id} value={m.id}>{m.vorname || ''} {m.nachname || m.name || ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('ausnahmen.grund', 'Grund')}</label>
            <input type="text" value={grundFilter} onChange={e => setGrundFilter(e.target.value)}
              placeholder={t('ausnahmen.grundPlaceholder', 'z.B. Arzt')}
              className="w-full px-3 py-1.5 text-sm rounded-lg border"
              style={{ borderColor: 'var(--border-line, #E5E7EB)', background: 'var(--bg-surface, white)', color: 'var(--text-primary, #1A1A2E)' }} />
          </div>
          <button onClick={load} disabled={loading}
            className="px-4 py-1.5 text-sm font-medium rounded-lg transition-colors"
            style={{ background: '#338edc', color: 'white', opacity: loading ? 0.5 : 1 }}>
            {loading ? t('common.loading', 'Laden...') : t('ausnahmen.anwenden', 'Anwenden')}
          </button>
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-surface-elevated rounded-card border border-line px-5 py-4">
            <div className="text-xs mb-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('ausnahmen.gesamt', 'Gemeldete Ausnahmen')}
            </div>
            <div className="text-2xl font-extrabold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {data.gesamt}
            </div>
          </div>
          <div className="bg-surface-elevated rounded-card border border-line px-5 py-4">
            <div className="text-xs mb-2" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('ausnahmen.topGruende', 'Häufigste Gründe')}
            </div>
            {data.top_gruende && data.top_gruende.length > 0 ? (
              <div className="space-y-1">
                {data.top_gruende.slice(0, 3).map((g, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="truncate" style={{ color: 'var(--text-secondary, #4B5563)' }}>{g.grund}</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{g.anzahl}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>–</div>
            )}
          </div>
          <div className="bg-surface-elevated rounded-card border border-line px-5 py-4">
            <div className="text-xs mb-2" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('ausnahmen.topMAs', 'Top Mitarbeiter')}
            </div>
            {data.top_mitarbeiter && data.top_mitarbeiter.length > 0 ? (
              <div className="space-y-1">
                {data.top_mitarbeiter.slice(0, 3).map((m, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="truncate" style={{ color: 'var(--text-secondary, #4B5563)' }}>{m.name}</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{m.anzahl}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>–</div>
            )}
          </div>
        </div>
      )}

      {/* Tabelle */}
      <div className="bg-surface-elevated rounded-card border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
              <th className="text-left px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('ausnahmen.zeitpunkt', 'Zeitpunkt')}
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('ausnahmen.mitarbeiter', 'Mitarbeiter')}
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('ausnahmen.typ', 'Typ')}
              </th>
              <th className="text-right px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('ausnahmen.minutenSeitStart', 'Min. seit Arbeitsbeginn')}
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('ausnahmen.grund', 'Grund')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="text-center py-8 text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('common.loading', 'Laden...')}
              </td></tr>
            )}
            {!loading && data && data.ausnahmen.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-sm" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                {t('ausnahmen.keine', 'Keine Ausnahmen im gewählten Zeitraum.')}
              </td></tr>
            )}
            {!loading && data && data.ausnahmen.map(a => (
              <tr key={a.id} className="border-b border-line/40">
                <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {fmtDateTime(a.zeitpunkt)}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <MitarbeiterAvatar mitarbeiterId={a.mitarbeiter_id} name={a.mitarbeiter_name} size={24} />
                    <span className="text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{a.mitarbeiter_name}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-xs" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {TYP_LABELS[a.typ] || a.typ}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {a.minuten_seit_start != null ? `${a.minuten_seit_start} min` : '–'}
                </td>
                <td className="px-4 py-2 text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {a.grund || <span style={{ color: 'var(--text-muted, #9CA3AF)' }}>–</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
